import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { categories, articles } from '@/mocks/articles';

const articleImages: Record<string, string> = {
  '2048-core-architecture-deep-dive': 'https://readdy.ai/api/search-image?query=Futuristic%20abstract%20processor%20chip%20architecture%20with%20warm%20orange%20geometric%20patterns%2C%20minimalist%20tech%20aesthetic%2C%20clean%20dark%20background%20with%20subtle%20glowing%20lines&width=800&height=400&seq=article-arch-01&orientation=landscape',
  '2048-core-ai-training-benchmark': 'https://readdy.ai/api/search-image?query=Abstract%20AI%20neural%20network%20visualization%20with%20warm%20orange%20and%20dark%20teal%20nodes%2C%20minimalist%20tech%20aesthetic%2C%20clean%20dark%20background&width=800&height=400&seq=article-ai-01&orientation=landscape',
  '2048-core-datacenter-deployment': 'https://readdy.ai/api/search-image?query=Futuristic%20data%20center%20server%20racks%20with%20warm%20orange%20accent%20lighting%2C%20minimalist%20tech%20aesthetic%2C%20clean%20geometric%20design&width=800&height=400&seq=article-dc-01&orientation=landscape',
  '2048-core-scientific-computing': 'https://readdy.ai/api/search-image?query=Abstract%20climate%20simulation%20grid%20visualization%20with%20warm%20orange%20geometric%20patterns%2C%20minimalist%20tech%20aesthetic%2C%20clean%20dark%20background&width=800&height=400&seq=article-sci-01&orientation=landscape',
  'many-core-programming-model': 'https://readdy.ai/api/search-image?query=Abstract%20parallel%20programming%20code%20blocks%20with%20warm%20orange%20and%20teal%20highlighting%2C%20minimalist%20tech%20aesthetic%2C%20clean%20dark%20background&width=800&height=400&seq=article-prog-01&orientation=landscape',
  '2048-core-memory-wall-solutions': 'https://readdy.ai/api/search-image?query=Abstract%20memory%20chip%20stack%20architecture%20with%20warm%20orange%20geometric%20patterns%2C%20minimalist%20tech%20aesthetic%2C%20clean%20dark%20background&width=800&height=400&seq=article-mem-01&orientation=landscape',
  '2048-core-security-isolation': 'https://readdy.ai/api/search-image?query=Abstract%20security%20shield%20with%20warm%20orange%20geometric%20chip%20patterns%2C%20minimalist%20tech%20aesthetic%2C%20clean%20dark%20background%2C%20modern%20cybersecurity%20design&width=800&height=400&seq=article-sec-01&orientation=landscape',
  '2048-core-interconnect-evolution': 'https://readdy.ai/api/search-image?query=Abstract%20network%20mesh%20topology%20with%20warm%20orange%20nodes%20and%20teal%20connections%2C%20minimalist%20tech%20aesthetic%2C%20clean%20dark%20background%2C%20modern%20interconnect%20design&width=800&height=400&seq=article-evo-01&orientation=landscape',
};

export default function CategoryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get('cat') || 'tech-architecture';
  const [isChanging, setIsChanging] = useState(false);

  const currentCategory = categories.find((c) => c.slug === activeCategory);
  const filteredArticles = articles.filter((a) => a.category === activeCategory);

  const handleCategoryChange = (slug: string) => {
    if (slug === activeCategory) return;
    setIsChanging(true);
    setSearchParams({ cat: slug });
    setTimeout(() => setIsChanging(false), 300);
  };

  return (
    <>
      <ScrollToTop />
      <main>
        <Navbar />

        <section className="pt-24 pb-16 md:pt-32 md:pb-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="mb-3">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: '分类浏览', href: '/categories' },
                  { label: currentCategory?.name || '' },
                ]}
              />
            </div>

            <div className="mt-6 mb-8">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">
                {currentCategory?.name}
              </h1>
              <p className="text-sm text-foreground-500 max-w-2xl">
                {currentCategory?.description}
              </p>
            </div>

            <div className="flex flex-wrap gap-2 mb-10">
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => handleCategoryChange(cat.slug)}
                  className={`px-4 py-2 rounded-full text-xs font-medium transition-all duration-300 whitespace-nowrap flex items-center gap-1.5 ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-50 text-foreground-600 hover:text-foreground-900 hover:bg-background-100 border border-background-200/70'
                  }`}
                >
                  <div className="w-3.5 h-3.5 flex items-center justify-center">
                    <i className={cat.icon}></i>
                  </div>
                  {cat.name}
                </button>
              ))}
            </div>

            <div className={`transition-opacity duration-300 ${isChanging ? 'opacity-0' : 'opacity-100'}`}>
              {filteredArticles.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {filteredArticles.map((article) => (
                    <Link
                      key={article.slug}
                      to={`/articles/${article.slug}`}
                      className="group bg-background-50 rounded-lg overflow-hidden transition-all duration-300 hover:bg-background-50/90 flex flex-col"
                    >
                      <div className="relative h-48 overflow-hidden">
                        <img
                          src={articleImages[article.slug]}
                          alt={article.title}
                          title={article.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-5 flex flex-col flex-1">
                        <div className="flex items-center gap-3 mb-3">
                          <span className="text-xs text-foreground-400">{article.date}</span>
                          <span className="text-xs text-foreground-300">·</span>
                          <span className="text-xs text-foreground-400">{article.readTime}</span>
                        </div>
                        <h3 className="font-heading font-semibold text-sm text-foreground-900 mb-2 leading-snug group-hover:text-primary-500 transition-colors line-clamp-2">
                          {article.title}
                        </h3>
                        <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2 mb-4 flex-1">
                          {article.excerpt}
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {article.tags.slice(0, 3).map((tag) => (
                            <span
                              key={tag}
                              className="px-2 py-0.5 bg-secondary-100 text-secondary-700 text-xs rounded-full"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                    <i className="ri-file-list-3-line text-xl text-foreground-300"></i>
                  </div>
                  <p className="text-foreground-400 text-sm">该分类暂无文章</p>
                </div>
              )}
            </div>
          </div>
        </section>

        <Footer />
      </main>
    </>
  );
}